import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { useMemo, useState } from "react";
import { useData } from "../../services/DataProvider";
import { getDefaultQuizId } from "../../utils/defaultQuizMapping";

const BackfillDefaultQuizStandard: React.FC = () => {
  const { quizzes, standards, adminCreateRecord } = useData();
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const backfillPlan = useMemo(() => {
    const quizById = new Map<string, typeof quizzes[number]>();
    quizzes.forEach((quiz) => {
      if (quiz.id) {
        quizById.set(quiz.id, quiz);
      }
    });

    const updatesByQuiz = new Map<string, Set<string>>();
    standards.forEach((standard) => {
      const quizId = getDefaultQuizId(standard.grade, standard.topic);
      if (!quizId || !standard.id) {
        return;
      }
      const quiz = quizById.get(quizId);
      if (!quiz) {
        return;
      }
      if (quiz.standardIDs?.includes(standard.id)) {
        return;
      }
      const set = updatesByQuiz.get(quizId) || new Set<string>();
      set.add(standard.id);
      updatesByQuiz.set(quizId, set);
    });

    const updates = Array.from(updatesByQuiz.entries()).map(([quizId, standardIds]) => ({
      quizId,
      standardIds: Array.from(standardIds),
    }));

    return {
      updates,
      quizzesAffected: updates.length,
      standardsToAssociate: updates.reduce((sum, update) => sum + update.standardIds.length, 0),
    };
  }, [quizzes, standards]);

  const runBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    try {
      await Promise.all(
        backfillPlan.updates.map(async ({ quizId, standardIds }) => {
          const quiz = quizzes.find((entry) => entry.id === quizId);
          if (!quiz) {
            return;
          }
          await adminCreateRecord("finlitri-quizzes", {
            ...quiz,
            standardIDs: [...(quiz.standardIDs || []), ...standardIds],
          });
        })
      );
      setResultMessage("Backfill completed successfully.");
    } catch (error) {
      setResultMessage(`Backfill failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Default Quiz Standard Backfill</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          This backfill adds each standard to the default quiz that matches its grade and topic,
          using the frontend default quiz mapping.
        </p>
        <p><b>Quizzes affected: </b> {backfillPlan.quizzesAffected}</p>
        <p><b>Standards to associate: </b> {backfillPlan.standardsToAssociate}</p>

        <IonButton
          expand="block"
          onClick={runBackfill}
          disabled={running || backfillPlan.standardsToAssociate === 0}
          aria-label="Run default quiz standard backfill"
          title="Run default quiz standard backfill"
        >
          {running ? "Running..." : "Run Backfill"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillDefaultQuizStandard;
